import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft } from "lucide-react";
import { FaHatWizard } from "react-icons/fa";
import { FiSettings } from "react-icons/fi";
import { styles } from "./styles";
import { ModeCard } from "./common/ModeCard";
import { Button } from "@/components/ui/button";
import { CreateLeagueWizard } from "./CreateLeagueWizard";
import { AdvancedLeagueEditor } from "./advanced/AdvancedLeagueEditor";
import { wizardLayout } from "./advanced/wizardLayout";
import type { LeagueFormValues } from "./leagueFormSchema";
import { useCreateLeagueMutation } from "../api/endpoints/leagues";
import { getUserIdFromToken } from "@/modules/user/userHooks";
import { useFacilitiesByUserId } from "@/modules/facility/facilityHooks";
import {
  extractServerErrorLines,
  isMissingParticipatingFacilities,
  participatingClubhouseFacilityIds,
  participatingIdsToSend,
  resolveLeagueTimezone,
} from "./helpers";
import { toCreateRoundBody } from "./advanced/roundMapping";
import {
  Clubhouse_LeagueFacilityScope,
  Clubhouse_LeagueWizardScheduleMode,
} from "../enums";
import { toGuidedLeagueSettings } from "./guidedLeagueSettings";
import { LeagueCreatedDialog } from "./common/LeagueCreatedDialog";
import { pendoTrackEventWithProperties } from "utils/pendo/pendoHelpers";
import { LeaguesV2Events } from "../analytics/leaguesV2Events";
import { useTrackLeaguesV2View } from "../analytics/useTrackLeaguesV2View";

type Stage = "select" | "wizard" | "advanced";
type Mode = "wizard" | "advanced" | null;

interface SelectStageProps {
  name: string;
  description: string;
  mode: Mode;
  onNameChange: (v: string) => void;
  onDescriptionChange: (v: string) => void;
  onModeChange: (m: Mode) => void;
  onContinue: () => void;
  onCancel: () => void;
}

function SelectStage({
  name,
  description,
  mode,
  onNameChange,
  onDescriptionChange,
  onModeChange,
  onContinue,
  onCancel,
}: SelectStageProps) {
  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-col gap-1.5">
        <label className={styles.labelCls} htmlFor="name">
          League Name
        </label>
        <input
          id="name"
          type="text"
          required
          value={name}
          onChange={(e) => onNameChange(e.target.value)}
          placeholder="e.g. Summer Stroke Play League"
          className={styles.inputCls}
        />
      </div>

      <div className="flex flex-col gap-1.5">
        <label className={styles.labelCls} htmlFor="description">
          Description
        </label>
        <textarea
          id="description"
          required
          value={description}
          onChange={(e) => onDescriptionChange(e.target.value)}
          rows={2}
          placeholder="A short description players will see when they view this league"
          className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm text-text-primary placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50 resize-none"
        />
      </div>

      <fieldset className="flex flex-col gap-2 border-0 p-0 m-0">
        <legend className={`${styles.labelCls} mb-0.5`}>Setup Type</legend>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <ModeCard
            icon={<FaHatWizard className="size-5" />}
            title="Guided Setup"
            description="Step through the essentials and launch in minutes."
            selected={mode === "wizard"}
            onClick={() => onModeChange("wizard")}
            banner="Recommended"
          />
          <ModeCard
            icon={<FiSettings className="size-5" />}
            title="Advanced Setup"
            description="Configure scoring, handicaps, rounds, and every setting."
            selected={mode === "advanced"}
            onClick={() => onModeChange("advanced")}
          />
        </div>
      </fieldset>

      <div className="flex justify-between">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          className="bg-primary/6 font-bold text-primary rounded-md px-4"
        >
          Cancel
        </Button>
        <div className="flex flex-col items-end gap-1.5">
          <Button
            type="button"
            disabled={!name.trim() || !mode || !description.trim()}
            onClick={onContinue}
            className="bg-primary poppins font-semibold tracking-tight text-primary-foreground rounded-md motion-safe:hover:-translate-y-px motion-safe:hover:shadow-md active:scale-[0.97]"
          >
            Continue
          </Button>
          {(!name.trim() || !description.trim()) && (
            <p className="text-xs text-text-muted">
              Add a name and description to continue.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

export function CreateLeaguePage() {
  const navigate = useNavigate();
  useTrackLeaguesV2View(LeaguesV2Events.VIEW_CREATE_LEAGUE);

  const [stage, setStage] = useState<Stage>("select");
  const [mode, setMode] = useState<Mode>("wizard");
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [createdLeague, setCreatedLeague] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [createLeague, { isLoading }] = useCreateLeagueMutation();
  const [userFacilities, facilitiesQueryState] =
    useFacilitiesByUserId(getUserIdFromToken());
  const hasMultipleFacilities = (userFacilities?.length ?? 0) > 1;

  const handleContinue = () => {
    if (!name.trim() || !mode || !description.trim()) {
      setSubmitError("Please fill in all fields");
      return;
    }
    setSubmitError(null);
    pendoTrackEventWithProperties(LeaguesV2Events.SELECT_CREATE_MODE, { mode });
    setStage(mode);
  };

  const handleBack = () => {
    setSubmitError(null);
    setStage("select");
  };

  const handleSubmit = async (values: LeagueFormValues) => {
    if (!values.facilityId) return;
    setSubmitError(null);
    // A multi-facility league is played at the facilities the operator checked
    // on the Location step — not at every facility they can see, which is what
    // Guided used to send. A single-facility league still sends none, so it is
    // created at the facility they are working in.
    const participating =
      values.facilityScope ===
      Clubhouse_LeagueFacilityScope.ANY_OF_MY_FACILITIES
        ? (participatingIdsToSend(values.participatingClubhouseFacilityIds) ??
          participatingClubhouseFacilityIds(
            values.facilityScope,
            userFacilities,
          ))
        : undefined;
    const facilitiesLoaded = !!facilitiesQueryState?.isFinished;
    // Wait for the facility list before deriving IANA. The multi-facility
    // participating guard only covers ANY_OF_MY_FACILITIES; a single-facility
    // submit can otherwise send a browser fallback while timeZoneIana is still
    // in flight.
    if (
      !facilitiesLoaded ||
      isMissingParticipatingFacilities(
        values.facilityScope,
        participating,
        facilitiesLoaded,
      )
    ) {
      setSubmitError(
        "Your facilities are still loading. Please try again in a moment.",
      );
      return;
    }
    try {
      const facilityTz = userFacilities.find(
        (f) => f.id === values.facilityId,
      )?.timeZoneIana;
      const timezone = resolveLeagueTimezone(facilityTz);
      const scheduleMode = values.wizard?.scheduleMode;
      const isFlex =
        scheduleMode === Clubhouse_LeagueWizardScheduleMode.SeasonFlex;
      // Season Flex is stored by rounds that omit both play windows; a filled
      // window with no schedule mode is persisted as a fixed league instead.
      const roundOptions = isFlex
        ? { timezone, omitPlayWindow: true }
        : { timezone };
      const rounds = values.seasonRounds ?? [];
      // Guided holds Create until its rounds exist, so this is unreachable from
      // the UI — but a league created without them is one with empty round
      // settings, which is the thing this flow exists to prevent. Refuse rather
      // than quietly let the backend generate settingless rounds.
      if (rounds.length === 0) {
        setSubmitError(
          "This league's rounds are not ready yet. Please try again in a moment.",
        );
        return;
      }
      const result = await createLeague({
        facilityId: values.facilityId,
        name: values.name,
        description: values.description,
        leagueStartDate: values.leagueStartDate,
        leagueStartDateTimezone: timezone,
        leagueEndDateTimezone: timezone,
        leagueEndDate: values.leagueEndDate,
        facilityScope: values.facilityScope,
        participatingClubhouseFacilityIds: participating,
        // Substitutes are a per-player role, not a Guided question. The create
        // flag defaults off when omitted, and a draft has no settings control
        // to turn it on, so Guided enables it the same way Advanced does.
        allowSubstitutes: true,
        // Guided and Advanced post the same shape: the league's settings, and
        // the schedule mode beside them. There is no wizard envelope.
        settings: toGuidedLeagueSettings(values),
        ...(scheduleMode ? { wizardScheduleMode: scheduleMode } : {}),
        // Guided generates its own rounds so they are created with settings
        // rather than empty (see useGuidedRoundPlan).
        seasonRoundCount: rounds.length,
        seasonRounds: rounds.map((round) =>
          toCreateRoundBody(round, roundOptions),
        ),
      }).unwrap();
      if (result?.id == null) {
        setSubmitError("The league could not be created. Please try again.");
        return;
      }
      setCreatedLeague({ id: String(result.id), name: values.name });
    } catch (err) {
      const issues = extractServerErrorLines(err);
      setSubmitError(
        issues.length > 0
          ? issues.join("\n")
          : "Failed to create league. Please try again.",
      );
    }
  };

  return (
    <div className={`w-full`}>
      <div
        className={`flex flex-col gap-6 px-8 py-4 ${
          stage === "advanced" ? wizardLayout.shell : "mx-auto w-full max-w-3xl"
        }`}
      >
        {/* Guided Setup titles each step itself, under its own Create League
            eyebrow, so the page heading would be a second title above it. */}
        {stage !== "wizard" && (
          <>
            <Button
              type="button"
              variant="link"
              onClick={() => navigate("/leagues")}
              className="w-fit px-0 h-auto gap-1 text-sm font-medium text-text-muted hover:text-text-primary hover:no-underline"
            >
              <ChevronLeft data-icon="inline-start" />
              Back to Leagues
            </Button>

            <div>
              <h1 className="text-2xl font-semibold text-text-primary brand-text tracking-tight">
                Create League
              </h1>
              {stage === "select" && (
                <p className="text-sm text-text-muted mt-1">
                  Name your league and choose how you would like to set it up.
                </p>
              )}
            </div>
          </>
        )}

        {stage === "select" && (
          <SelectStage
            name={name}
            description={description}
            mode={mode}
            onNameChange={setName}
            onDescriptionChange={setDescription}
            onModeChange={setMode}
            onContinue={handleContinue}
            onCancel={() => navigate("/leagues")}
          />
        )}

        {stage === "wizard" && (
          <CreateLeagueWizard
            initialName={name}
            initialDescription={description}
            hasMultipleFacilities={hasMultipleFacilities}
            onSubmit={handleSubmit}
            onBack={handleBack}
            isSubmitting={isLoading}
          />
        )}

        {stage === "advanced" && (
          <AdvancedLeagueEditor
            mode="create"
            initialName={name}
            initialDescription={description}
            hasMultipleFacilities={hasMultipleFacilities}
            onExit={() => navigate("/leagues")}
            onGoToDashboard={() => navigate("/leagues")}
            onOpenLeague={(id) => navigate(`/leagues/${id}`)}
          />
        )}

        {submitError && (
          <p className="text-sm text-destructive bg-destructive/10 rounded-md px-3 py-2">
            {submitError}
          </p>
        )}
      </div>

      <LeagueCreatedDialog
        open={createdLeague != null}
        variant="draft"
        leagueName={createdLeague?.name}
        onGoToDashboard={() => navigate("/leagues")}
        onOpenLeague={() => {
          if (createdLeague) navigate(`/leagues/${createdLeague.id}`);
        }}
      />
    </div>
  );
}
